import type { RaciAssignment, RaciPerson, RaciRowItem, RaciUser } from '../../types/raci';
import { raciActions } from '../../stores/raciStore';
import { RaciPersonPicker } from './RaciPersonPicker';

interface RaciRowProps {
  item: RaciRowItem;
  assignment: RaciAssignment | undefined;
  users: RaciUser[];
  canEdit: boolean;
  dimmed?: boolean;
}

const ROLE_LABELS: { key: 'responsible' | 'accountable' | 'consulted' | 'informed'; label: string; abbr: string; color: string }[] = [
  { key: 'responsible', label: 'Responsible', abbr: 'R', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'accountable', label: 'Accountable', abbr: 'A', color: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'consulted', label: 'Consulted', abbr: 'C', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'informed', label: 'Informed', abbr: 'I', color: 'bg-green-50 text-green-700 border-green-200' },
];

export function RaciRow({ item, assignment, users, canEdit, dimmed = false }: RaciRowProps) {
  function handleChange(
    role: 'responsible' | 'accountable' | 'consulted' | 'informed',
    people: RaciPerson[],
  ) {
    if (role === 'accountable') {
      raciActions.setAccountable(item.key, people.length > 0 ? people[0] : null);
    } else {
      raciActions.setAssignment(item.key, role, people);
    }
  }

  function getSelected(role: 'responsible' | 'accountable' | 'consulted' | 'informed'): RaciPerson[] {
    if (!assignment) return [];
    if (role === 'accountable') {
      return assignment.accountable ? [assignment.accountable] : [];
    }
    return assignment[role] || [];
  }

  const hasAnyAssignment = assignment && (
    assignment.responsible.length > 0 ||
    assignment.accountable !== null ||
    assignment.consulted.length > 0 ||
    assignment.informed.length > 0
  );

  return (
    <div className={`rounded-lg border p-4 transition-shadow ${
      dimmed
        ? 'bg-gray-50 border-gray-200 opacity-60'
        : 'bg-white border-gray-200 hover:shadow-sm'
    }`}>
      {/* Item info row */}
      <div className="mb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h4 className={`text-sm font-semibold leading-tight ${dimmed ? 'text-gray-400' : 'text-gray-900'}`}>
              {item.label}
            </h4>
            {item.sublabel && (
              <p className={`text-xs mt-0.5 line-clamp-2 ${dimmed ? 'text-gray-300' : 'text-gray-500'}`}>
                {item.sublabel}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {dimmed && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200">
                Not Used
              </span>
            )}
            {hasAnyAssignment && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                dimmed ? 'bg-gray-100 text-gray-400' : 'bg-purple-100 text-purple-700'
              }`}>
                Assigned
              </span>
            )}
          </div>
        </div>
      </div>

      {/* RACI columns */}
      <div className="grid grid-cols-4 gap-3">
        {ROLE_LABELS.map(({ key, label, abbr, color }) => (
          <div key={key} className={`rounded-lg border p-2 ${dimmed ? 'bg-gray-50 text-gray-400 border-gray-200' : color}`}>
            <div className="flex items-center gap-1 mb-1.5">
              <span className="text-xs font-bold">{abbr}</span>
              <span className="text-xs font-medium opacity-70">{label}</span>
            </div>
            <RaciPersonPicker
              users={users}
              selected={getSelected(key)}
              onChange={(people) => handleChange(key, people)}
              multiple={key !== 'accountable'}
              disabled={!canEdit}
              label={label}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
